import i18n from '@/i18n';
import Device from '@/wrappers/Device';
import CashRegister from '@/models/CashRegister';
import Store from '@/store';

export default {
    device: null,

    queue: [],
    curJob: false,
    initialized: false,
    busy: false,
    queueServingSetup: false,
    basicInformation: null,

    async init() {
        if(this.busy) return

        const noncashOnly = !!Store.getters.tenant?.venue?.settings?.noncash_only;
        const device = Device.get();
        if(locale !== 'ba' || noncashOnly || device?.platform !== 'electron') return

        if(!this.queueServingSetup) {
            this.setUpQueueServing()
        }

        const setupResult = await this.setup()
        if(!setupResult) {
            this.basicInformation = null
            this.initialized = false
            return
        }

        this.busy = true

        const info = await this.getBasicInformation(true)
        this.basicInformation = info
        this.initialized = info?.data?.ibfm && info.data.ibfm.slice(0, 7) === CashRegister.current()?.activation_code.slice(0, 7)

        this.busy = false
    },

    async setup(printer = null) {
        if(!printer) {
            printer = PrintService.getAssignedPrinter('receipt');
        }

        if(printer?.printer?.name !== 'fp1') return false

        const { requestPath, responsePath } = printer?.options?.other || {}
        await window.electron.invoke('fsl-setup', { requestPath, responsePath })
        return true
    },

    async createInvoice(invoice, queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('createInvoice', { invoice })

        let result = null
        if((invoice.invoiceType === 'Normal' || invoice.invoiceType === 'Advance') && invoice.transactionType === 'Sale') {
            result = await window.electron.invoke('fsl-print-fiscal-receipt', invoice);
        } else if((invoice.invoiceType === 'Normal' || invoice.invoiceType === 'Advance') && invoice.transactionType === 'Refund') {
            result = await window.electron.invoke('fsl-print-reclamatory-receipt', invoice);
        } else if(invoice.invoiceType === 'Copy') {
            result = await window.electron.invoke('fsl-print-duplicate-fiscal-receipt', invoice.referentDocumentNumber, invoice.transactionType === 'Refund');
        } else {
            result = { success: false, error: i18n.t('fiscal.fiscal_printer_not_supported') }
        }

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async printReceiptCopy(refNumber, isRefund = false, queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('printReceiptCopy', { refNumber, isRefund })
        const result = await window.electron.invoke('fsl-print-duplicate-fiscal-receipt', refNumber, isRefund);

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async printText(text, forceCheck = false, queue = true) {
        if(!this.initialized && !forceCheck) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('printText', { text, forceCheck })
        const result = await window.electron.invoke('fsl-print-text', text);

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async printCurrentStateReport(queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('printCurrentStateReport')
        const result = await window.electron.invoke('fsl-print-balance-sheet');

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async printDailyReport(queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('printDailyReport')
        const result = await window.electron.invoke('fsl-print-daily-report');

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async printPeriodicReport(period, queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('printPeriodicReport', { period })
        const result = await window.electron.invoke('fsl-print-periodic-report', period);

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async getBasicInformation(forceCheck = false, queue = true) {
        if(!this.initialized && !forceCheck) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('getBasicInformation', { forceCheck })
        const result = await window.electron.invoke('fsl-get-basic-information')

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async withdrawMoney(amount, queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('withdrawMoney', { amount })
        const result = await window.electron.invoke('fsl-withdraw-money', amount)

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    async depositMoney(amount, queue = true) {
        if(!this.initialized) return this.formatError({ code: 'not_supported', message: i18n.t('cash_register.error.not_supported') })

        if(queue) return this.enqueue('depositMoney', { amount })
        const result = await window.electron.invoke('fsl-deposit-money', amount)

        if(result.error) {
            return this.formatError(result.error)
        }

        return { data: result.data, success: result.success }
    },

    formatError(error) {
        console.log('Response error: ' + (error.message || error.code))
        return { data: null, success: false, error: error }
    },

    /**
     * Add fiscal service job to the queue and return
     * the result after the job has been served
     */
    async enqueue(name, data) {
        const id = Math.random().toString(36).replace(/[^a-z]+/g, '').substring(0, 5)
        let job = { id, name, data, promise: this.defer() }
        this.queue.push(job);
        return await job.promise;
    },

    /**
     * Serve Print Jobs
     */
    setUpQueueServing(interval = 250, timeout = 60000) {
        this.queueServingSetup = true

        let busyTime = 0
        setInterval(() => {
            if (this.curJob && (busyTime + interval) < timeout) {
                busyTime += interval
                return
            }

            this.curJob = null
            busyTime = 0

            this.serveQueue()
        }, interval);
    },

    /**
     * Serve the next available job in the queue
     */
    async serveQueue() {
        if(this.queue.length === 0) return

        const job = this.queue[0]
        this.queue.shift()

        let result = null
        this.curJob = job.id

        if(job.name === 'createInvoice') result = await this.createInvoice(job.data.invoice, false)
        else if(job.name === 'printReceiptCopy') result = await this.printReceiptCopy(job.data.refNumber, job.data.isRefund, false)
        else if(job.name === 'printText') result = await this.printText(job.data.text, job.data.forceCheck, false)
        else if(job.name === 'printCurrentStateReport') result = await this.printCurrentStateReport(false)
        else if(job.name === 'printDailyReport') result = await this.printDailyReport(false)
        else if(job.name === 'printPeriodicReport') result = await this.printPeriodicReport(job.data.period, false)
        else if(job.name === 'getBasicInformation') result = await this.getBasicInformation(job.data.forceCheck, false)
        else if(job.name === 'withdrawMoney') result = await this.withdrawMoney(job.data.amount, false)
        else if(job.name === 'depositMoney') result = await this.depositMoney(job.data.amount, false)
        else result = { success: false, error: { code: 'invalid_action', message: 'Action not valid' } }

        job.promise.resolve(result)

        if(this.curJob === job.id) {
            this.curJob = null
        }
    },

    /**
     * Create deferred promise that should manually be resolved
     */
    defer() {
        let res, rej;

        let promise = new Promise((resolve, reject) => {
            res = resolve;
            rej = reject;
        });

        promise.resolve = res;
        promise.reject = rej;

        return promise;
    }
}
